import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ClerkProvider } from '@clerk/clerk-react';
import App from './App';
import './index.css';

// Error boundary for catching rendering errors
class AppErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('App error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-legacy-warm-white">
          <div className="max-w-md w-full p-6 bg-white rounded-xl shadow-lg border border-primary-200">
            <h1 className="text-2xl font-bold text-accent-wood mb-4">Something went wrong</h1>
            <p className="text-primary-600">
              {this.state.error?.message || 'An unexpected error occurred. Please try refreshing the page.'}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-accent-wood hover:bg-accent-wood-light text-white rounded-lg transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

console.log('Environment check:', {
  clerkKey: clerkPubKey ? 'Present' : 'Missing',
  supabaseUrl: import.meta.env.VITE_SUPABASE_URL ? 'Present' : 'Missing',
  supabaseKey: import.meta.env.VITE_SUPABASE_ANON_KEY ? 'Present' : 'Missing',
  nodeEnv: import.meta.env.MODE
});

if (!clerkPubKey) {
  console.error('Missing Clerk Publishable Key - app will not function properly');
  // Don't throw error, just log it
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <AppErrorBoundary>
    <ClerkProvider
      publishableKey={clerkPubKey}
      appearance={{
        baseTheme: undefined,
        elements: {
          formButtonPrimary: 'bg-accent-wood hover:bg-accent-wood-light',
          footerActionLink: 'text-accent-wood hover:text-accent-wood-light'
        }
      }}
    >
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <App />
      </BrowserRouter>
    </ClerkProvider>
  </AppErrorBoundary>
); 